import React, { useState, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { FiCode, FiCopy, FiCheck, FiRefreshCw, FiEye, FiLayout, FiSliders } from 'react-icons/fi';
import { useAuth } from '../../hooks/useAuth';

const EmbedConfigurator = () => {
  const { dealerId } = useAuth();
  const [copied, setCopied] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const iframeRef = useRef(null);

  const [config, setConfig] = useState({
    columns: 3,
    limit: 12,
    theme: 'light',
    accentColor: '#059669',
    hidePrice: false,
    hideBranding: false,
    height: 800,
    borderRadius: 8
  });

  const siteUrl = window.location.origin;

  const embedUrl = useMemo(() => {
    if (!dealerId) return '';
    const params = new URLSearchParams();
    if (config.columns !== 3) params.set('cols', config.columns);
    if (config.limit !== 50) params.set('limit', config.limit);
    if (config.theme !== 'light') params.set('theme', config.theme);
    if (config.accentColor !== '#059669') params.set('accent', config.accentColor);
    if (config.hidePrice) params.set('hidePrice', 'true');
    if (config.hideBranding) params.set('hideBranding', 'true');
    const qs = params.toString();
    return `${siteUrl}/embed/${dealerId}${qs ? '?' + qs : ''}`;
  }, [dealerId, config, siteUrl]);

  const embedCode = useMemo(() => {
    if (!embedUrl) return '';
    return `<iframe\n  src="${embedUrl}"\n  width="100%"\n  height="${config.height}"\n  frameborder="0"\n  style="border: none; border-radius: ${config.borderRadius}px; overflow: hidden;"\n  loading="lazy"\n  title="Shed Inventory"\n></iframe>`;
  }, [embedUrl, config.height, config.borderRadius]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = embedCode;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const refreshPreview = () => setPreviewKey(k => k + 1);

  const update = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const presetColors = [
    { label: 'Emerald', value: '#059669' },
    { label: 'Blue', value: '#2563eb' },
    { label: 'Indigo', value: '#4f46e5' },
    { label: 'Rose', value: '#e11d48' },
    { label: 'Amber', value: '#d97706' },
    { label: 'Slate', value: '#475569' }
  ];

  if (!dealerId) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
        <p className="text-amber-700 text-sm">
          Embed widget is only available for dealers with an active account.
        </p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-gray-200 overflow-hidden"
    >
      {/* Header */}
      <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-teal-50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
            <FiCode className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 text-base">Website Embed Widget</h3>
            <p className="text-sm text-gray-500">Display your ShedX inventory on your own website</p>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Two column: Controls + Preview */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">

          {/* LEFT: Controls */}
          <div className="space-y-5">
            {/* Section: Layout */}
            <div>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <FiLayout size={12} /> Layout
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Columns</label>
                  <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                    {[2, 3, 4].map(n => (
                      <button
                        key={n}
                        onClick={() => update('columns', n)}
                        className={`flex-1 py-2 text-sm font-medium transition-colors ${
                          config.columns === n
                            ? 'bg-gray-900 text-white'
                            : 'bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Sheds</label>
                  <select
                    value={config.limit}
                    onChange={e => update('limit', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    {[6, 9, 12, 18, 24, 50].map(n => (
                      <option key={n} value={n}>{n} sheds</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Height (px)</label>
                  <input
                    type="number"
                    min="400"
                    max="2000"
                    step="50"
                    value={config.height}
                    onChange={e => update('height', parseInt(e.target.value) || 800)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Corner Radius</label>
                  <input
                    type="range"
                    min="0"
                    max="24"
                    value={config.borderRadius}
                    onChange={e => update('borderRadius', parseInt(e.target.value))}
                    className="w-full mt-2 accent-emerald-600"
                  />
                  <span className="text-xs text-gray-400">{config.borderRadius}px</span>
                </div>
              </div>
            </div>

            {/* Section: Style */}
            <div>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <FiSliders size={12} /> Style
              </h4>

              {/* Theme toggle */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Theme</label>
                <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                  {['light', 'dark'].map(t => (
                    <button
                      key={t}
                      onClick={() => update('theme', t)}
                      className={`flex-1 py-2 text-sm font-medium capitalize transition-colors ${
                        config.theme === t
                          ? 'bg-gray-900 text-white'
                          : 'bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              {/* Accent color */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">Accent Color</label>
                <div className="flex items-center gap-2 flex-wrap">
                  {presetColors.map(c => (
                    <button
                      key={c.value}
                      onClick={() => update('accentColor', c.value)}
                      className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${
                        config.accentColor === c.value ? 'border-gray-900 scale-110' : 'border-transparent'
                      }`}
                      style={{ backgroundColor: c.value }}
                      title={c.label}
                    />
                  ))}
                  <div className="relative">
                    <input
                      type="color"
                      value={config.accentColor}
                      onChange={e => update('accentColor', e.target.value)}
                      className="w-8 h-8 rounded-full cursor-pointer border-0 p-0"
                      title="Custom color"
                    />
                  </div>
                </div>
              </div>

              {/* Toggles */}
              <div className="space-y-2">
                <label className="flex items-center justify-between p-3 rounded-lg bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors">
                  <span className="text-sm text-gray-700">Show prices</span>
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={!config.hidePrice}
                      onChange={e => update('hidePrice', !e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-gray-300 rounded-full peer-checked:bg-emerald-500 transition-colors"></div>
                    <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow peer-checked:translate-x-4 transition-transform"></div>
                  </div>
                </label>
                <label className="flex items-center justify-between p-3 rounded-lg bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors">
                  <span className="text-sm text-gray-700">Show "Powered by ShedX"</span>
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={!config.hideBranding}
                      onChange={e => update('hideBranding', !e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-gray-300 rounded-full peer-checked:bg-emerald-500 transition-colors"></div>
                    <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow peer-checked:translate-x-4 transition-transform"></div>
                  </div>
                </label>
              </div>
            </div>
          </div>

          {/* RIGHT: Preview */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                <FiEye size={12} /> Live Preview
              </h4>
              <button
                onClick={refreshPreview}
                className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 transition-colors"
              >
                <FiRefreshCw size={11} /> Refresh
              </button>
            </div>
            <div
              className="border border-gray-200 rounded-xl overflow-hidden bg-gray-100"
              style={{ height: Math.min(config.height, 520) }}
            >
              <iframe
                key={previewKey}
                ref={iframeRef}
                src={embedUrl}
                width="100%"
                height="100%"
                frameBorder="0"
                style={{
                  border: 'none',
                  borderRadius: `${config.borderRadius}px`,
                  transform: 'scale(0.75)',
                  transformOrigin: 'top left',
                  width: '133.33%',
                  height: '133.33%'
                }}
                title="Widget Preview"
                loading="lazy"
              />
            </div>
            <p className="text-xs text-gray-400 mt-2 text-center">
              Preview is scaled to 75%. Actual embed renders at full size.
            </p>
          </div>
        </div>

        {/* Embed Code */}
        <div className="mt-6 pt-6 border-t border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <FiCode size={14} className="text-emerald-600" />
              Your Embed Code
            </h4>
            <button
              onClick={handleCopy}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                copied
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-gray-900 text-white hover:bg-gray-800'
              }`}
            >
              {copied ? <FiCheck size={14} /> : <FiCopy size={14} />}
              {copied ? 'Copied!' : 'Copy Code'}
            </button>
          </div>
          <div className="relative">
            <pre className="bg-gray-950 text-gray-300 rounded-xl p-4 text-xs leading-relaxed overflow-x-auto font-mono">
              <code>{embedCode}</code>
            </pre>
          </div>
          <p className="text-xs text-gray-400 mt-3">
            Paste this code into your website's HTML where you want the inventory to appear.
            Works with WordPress, Wix, Squarespace, Shopify and any HTML page.
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default EmbedConfigurator;
